import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import type { User, AuthState } from '../../types/auth'

interface AuthContextType extends AuthState {
  refreshAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  })

  const refreshAuth = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true }))
      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()

      const user: User = {
        username: currentUser.username,
        email: currentUser.signInDetails?.loginId ?? '',
        sub: session.tokens?.idToken?.payload.sub as string ?? '',
      }

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error) {
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  }

  useEffect(() => {
    refreshAuth()
  }, [])

  return (
    <AuthContext.Provider value={{ ...authState, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
